import { useEffect, useState } from "react";
import { TransformWrapper, TransformComponent, useControls } from "react-zoom-pan-pinch";
import { ZoomIn, ZoomOut, RotateCcw, ChevronLeft, ChevronRight, Download, FolderDown, Loader2, ImageOff } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "./ui/dialog";
import { useAuthedBlobUrl } from "../hooks/useAuthedImage";
import api, { saveBlob } from "../lib/api";

const btn =
  "flex items-center justify-center h-9 w-9 rounded-full bg-background/80 backdrop-blur border border-border hover:bg-background transition-colors disabled:opacity-50";

// An entry is a plain src string, or { src, authed?, label?, filename? } (authed = owner-only path fetched with the session headers).
const entryOf = (e) => (typeof e === "string" ? { src: e } : e || {});

const Controls = ({ onDownload, downloadBusy, onDownloadAll, downloadAllBusy, filename }) => {
  const { zoomIn, zoomOut, resetTransform } = useControls();
  return (
    <div className="absolute top-3 left-1/2 -translate-x-1/2 z-20 flex gap-2">
      <button type="button" data-testid="lightbox-zoom-in" aria-label="Zoom in" className={btn} onClick={() => zoomIn()}>
        <ZoomIn className="h-4 w-4" />
      </button>
      <button type="button" data-testid="lightbox-zoom-out" aria-label="Zoom out" className={btn} onClick={() => zoomOut()}>
        <ZoomOut className="h-4 w-4" />
      </button>
      <button type="button" data-testid="lightbox-reset" aria-label="Reset zoom" className={btn} onClick={() => resetTransform()}>
        <RotateCcw className="h-4 w-4" />
      </button>
      {onDownload && (
        <button type="button" data-testid="lightbox-download" aria-label={`Download ${filename || "image"} (D)`} title={`Download ${filename || "image"} · D`} className={btn} onClick={onDownload} disabled={downloadBusy}>
          {downloadBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
        </button>
      )}
      {onDownloadAll && (
        <button type="button" data-testid="lightbox-download-all" aria-label="Download all images" title="Download all images (ZIP)" className={`${btn} w-auto px-3 gap-1.5 text-xs font-semibold`} onClick={onDownloadAll} disabled={downloadAllBusy}>
          {downloadAllBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <FolderDown className="h-4 w-4" />} All
        </button>
      )}
    </div>
  );
};

const Picture = ({ entry, alt }) => {
  const authed = useAuthedBlobUrl(entry.authed ? entry.src : null, !!entry.authed);
  const src = entry.authed ? authed.src : entry.src;
  if (entry.authed && authed.error) {
    return (
      <p data-testid="lightbox-error" className="flex items-center gap-2 text-sm text-muted-foreground"><ImageOff className="h-4 w-4" /> This image could not be loaded.</p>
    );
  }
  if (!src) {
    return <Loader2 data-testid="lightbox-loading" className="h-8 w-8 animate-spin text-muted-foreground" />;
  }
  return <img src={src} alt={alt} draggable={false} data-testid="lightbox-image" className="max-h-[80vh] max-w-full object-contain select-none" />;
};

// Reusable zoomable/pannable image pop-up. Pass an array of image srcs; when there is more than one,
// previous/next arrows + Arrow keys step through them (used by the turntable). Full-resolution src expected.
// `D` downloads the image on screen; `onDownloadAll` adds a "Download all" (ZIP) button.
export const ImageLightbox = ({ open, onOpenChange, images = [], index = 0, onIndexChange, alt = "image", onDownloadAll, downloadAllBusy = false }) => {
  const multi = images.length > 1;
  const safeIndex = Math.min(Math.max(index, 0), Math.max(images.length - 1, 0));
  const entry = entryOf(images[safeIndex]);
  const [busy, setBusy] = useState(false);
  const label = entry.label ? `${entry.label} — ${alt}` : alt;

  const download = async () => {
    if (!entry.src || busy) return;
    setBusy(true);
    try {
      const blob = entry.authed ? (await api.get(entry.src, { responseType: "blob" })).data : await (await fetch(entry.src)).blob();
      saveBlob(blob, entry.filename || `${(entry.label || alt || "image").toLowerCase().replace(/[^a-z0-9]+/g, "_")}.png`);
    } catch {
      /* the button simply stays available */
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.target && /^(input|textarea)$/i.test(e.target.tagName)) return;
      if (multi && e.key === "ArrowLeft") onIndexChange?.((safeIndex - 1 + images.length) % images.length);
      if (multi && e.key === "ArrowRight") onIndexChange?.((safeIndex + 1) % images.length);
      if (e.key === "d" || e.key === "D") download();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, multi, safeIndex, images.length, onIndexChange, entry.src]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="image-lightbox"
        className="max-w-[95vw] sm:max-w-4xl w-full p-0 bg-background border-border overflow-hidden"
      >
        <DialogTitle className="sr-only">Image preview</DialogTitle>
        <div className="relative h-[80vh]">
          {entry.src && (
            <TransformWrapper
              key={safeIndex}
              minScale={1}
              maxScale={6}
              centerOnInit
              doubleClick={{ mode: "reset" }}
              wheel={{ step: 0.2 }}
            >
              <Controls onDownload={download} downloadBusy={busy} onDownloadAll={onDownloadAll} downloadAllBusy={downloadAllBusy} filename={entry.filename} />
              <TransformComponent
                wrapperClass="!w-full !h-full"
                contentClass="!w-full !h-full flex items-center justify-center"
              >
                <Picture entry={entry} alt={label} />
              </TransformComponent>
            </TransformWrapper>
          )}
          {entry.label && (
            <span data-testid="lightbox-label" className="absolute top-3 left-3 z-20 rounded-full bg-background/80 backdrop-blur border border-border px-3 py-1 text-xs font-mono2 uppercase tracking-widest">
              {entry.label}
            </span>
          )}

          {multi && (
            <>
              <button
                type="button"
                data-testid="lightbox-prev"
                aria-label="Previous image"
                className={`${btn} absolute left-3 top-1/2 -translate-y-1/2 z-20`}
                onClick={() => onIndexChange?.((safeIndex - 1 + images.length) % images.length)}
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
              <button
                type="button"
                data-testid="lightbox-next"
                aria-label="Next image"
                className={`${btn} absolute right-3 top-1/2 -translate-y-1/2 z-20`}
                onClick={() => onIndexChange?.((safeIndex + 1) % images.length)}
              >
                <ChevronRight className="h-5 w-5" />
              </button>
              <span
                data-testid="lightbox-counter"
                className="absolute bottom-3 left-1/2 -translate-x-1/2 z-20 rounded-full bg-background/80 backdrop-blur border border-border px-3 py-1 text-xs font-mono2"
              >
                {safeIndex + 1} / {images.length}
              </span>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
